import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  COMPETENCY_AUDIENCE_OPTIONS,
  EVALUATION_FORM_TYPE_OPTIONS,
  EvaluationForm,
  EvaluationFormAvailableQuestion,
  EvaluationCycle,
  getEvaluationCycle,
  getEvaluationForm,
  listEvaluationFormAvailableQuestions,
  replaceEvaluationFormQuestions,
  updateEvaluationForm,
} from '../../services/peoplePerformanceApi';

function formTypeLabel(value?: string | null): string {
  return EVALUATION_FORM_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '—';
}

function audienceLabel(value?: string | null): string {
  return COMPETENCY_AUDIENCE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '—';
}

export function EvaluationFormDetailPage() {
  const { cycleId, formId } = useParams();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('people.evaluations');
  const {
    formExpanded: metaExpanded,
    expandForm: expandMeta,
    collapseForm: collapseMeta,
    toggleOrCancel: toggleOrCancelMeta,
  } = useCollapsibleItemForm();
  const {
    formExpanded: questionsExpanded,
    expandForm: expandQuestions,
    collapseForm: collapseQuestions,
    toggleOrCancel: toggleOrCancelQuestions,
  } = useCollapsibleItemForm();
  const [cycle, setCycle] = useState<EvaluationCycle | null>(null);
  const [cycleLoading, setCycleLoading] = useState(true);
  const [form, setForm] = useState<EvaluationForm | null>(null);
  const [formLoading, setFormLoading] = useState(true);
  const [availableQuestions, setAvailableQuestions] = useState<EvaluationFormAvailableQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const canEditContent = canWrite && cycle?.status === 'Draft';

  const questionsByIndicator = useMemo(() => {
    const groups = new Map<string, EvaluationFormAvailableQuestion[]>();
    for (const question of availableQuestions) {
      const key = question.indicatorName || 'Sem indicador';
      const list = groups.get(key) ?? [];
      list.push(question);
      groups.set(key, list);
    }
    return Array.from(groups.entries()).map(([indicatorName, questions]) => ({
      indicatorName,
      questions: questions.sort((a, b) => a.competencyName.localeCompare(b.competencyName, 'pt-BR')),
    }));
  }, [availableQuestions]);

  async function loadCycle(): Promise<void> {
    if (!cycleId) return;
    setCycleLoading(true);
    try {
      setCycle(await getEvaluationCycle(cycleId));
    } catch {
      setCycle(null);
      toast.error('Falha ao carregar ciclo.');
    } finally {
      setCycleLoading(false);
    }
  }

  async function loadForm(): Promise<void> {
    if (!cycleId || !formId) return;
    setFormLoading(true);
    try {
      const result = await getEvaluationForm(cycleId, formId);
      setForm(result);
      expandMeta();
    } catch {
      setForm(null);
      toast.error('Falha ao carregar formulário.');
    } finally {
      setFormLoading(false);
    }
  }

  async function loadQuestions(): Promise<void> {
    if (!cycleId || !formId) return;
    setQuestionsLoading(true);
    try {
      const available = await listEvaluationFormAvailableQuestions(cycleId, formId);
      setAvailableQuestions(available);
      setSelectedQuestionIds(
        available.filter((item) => item.selected).map((item) => item.competencyQuestionId),
      );
      expandQuestions();
    } catch {
      setAvailableQuestions([]);
      setSelectedQuestionIds([]);
      toast.error('Falha ao carregar perguntas do formulário.');
    } finally {
      setQuestionsLoading(false);
    }
  }

  useEffect(() => {
    void loadCycle();
    void loadForm();
    void loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, formId]);

  function toggleQuestion(questionId: string): void {
    setSelectedQuestionIds((current) =>
      current.includes(questionId)
        ? current.filter((id) => id !== questionId)
        : [...current, questionId],
    );
  }

  async function handleSaveMeta(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!cycleId || !formId || !form || !canEditContent) return;
    setSaving(true);
    try {
      const updated = await updateEvaluationForm(cycleId, formId, {
        name: form.name,
        formType: form.formType,
        audience: form.audience,
        sortOrder: form.sortOrder,
      });
      setForm(updated);
      await loadQuestions();
      toast.success('Formulário atualizado. Perguntas disponíveis recalculadas pelo nível.');
    } catch {
      toast.error('Falha ao atualizar formulário.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveQuestions(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!cycleId || !formId || !canEditContent) return;
    setSaving(true);
    try {
      await replaceEvaluationFormQuestions(cycleId, formId, selectedQuestionIds);
      await Promise.all([loadForm(), loadQuestions()]);
      toast.success('Perguntas do formulário salvas.');
    } catch {
      toast.error('Falha ao salvar perguntas do formulário.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageShell>
      <PageHeader
        title={formLoading ? 'Carregando formulário...' : form?.name ?? 'Formulário não encontrado'}
        description={
          form
            ? `Tipo: ${formTypeLabel(form.formType)} · Nível: ${audienceLabel(form.audience)} · Ciclo: ${cycle?.name ?? (cycleLoading ? '...' : '—')}`
            : 'Configuração do formulário de avaliação'
        }
        actions={(
          <Link to={`/people/evaluation-cycles/${cycleId}`} className="erp-btn-secondary text-sm">
            Voltar ao ciclo
          </Link>
        )}
      />

      {canEditContent && form ? (
        <CollapsibleFormSection
          title="Editar formulário"
          expanded={metaExpanded}
          onToggle={() => toggleOrCancelMeta(() => undefined)}
          isEditing
          editingSummary="Clique para expandir e editar nome, tipo e nível"
          className="mb-4"
        >
          <form onSubmit={(event) => void handleSaveMeta(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label sm:col-span-2">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Tipo de avaliação
                <select
                  className="erp-input"
                  value={form.formType}
                  onChange={(event) => setForm({ ...form, formType: event.target.value })}
                  required
                >
                  {EVALUATION_FORM_TYPE_OPTIONS.filter((option) => {
                    if (!cycle) return true;
                    if (option.value === 'Self') return !!cycle.enableSelfEvaluation;
                    if (option.value === 'Manager') return !!cycle.enableManagerEvaluation;
                    if (option.value === 'Peers') return !!cycle.enablePeersEvaluation;
                    if (option.value === 'Upward') return !!cycle.enableUpwardEvaluation;
                    return false;
                  }).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Nível do avaliado
                <select
                  className="erp-input"
                  value={form.audience || 'Colaborador'}
                  onChange={(event) => setForm({ ...form, audience: event.target.value })}
                  required
                >
                  <option value="Colaborador">Colaborador</option>
                  <option value="Gestor">Gestor</option>
                  <option value="Executivo">Executivo</option>
                </select>
              </label>
            </div>
            <FormActionBar
              onCancel={() => collapseMeta(() => undefined)}
              className="erp-btn-actions mt-4"
            >
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : 'Atualizar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : formLoading ? (
        <section className="mb-4 rounded-xl border border-secondary-200 bg-white p-4">
          <p className="text-sm text-secondary-500">Carregando formulário...</p>
        </section>
      ) : form ? (
        <section className="mb-4 rounded-xl border border-secondary-200 bg-white p-4">
          <p className="text-sm text-secondary-600">
            Tipo: <strong>{formTypeLabel(form.formType)}</strong>
            {' · '}
            Nível: <strong>{audienceLabel(form.audience)}</strong>
          </p>
        </section>
      ) : (
        <section className="mb-4 rounded-xl border border-secondary-200 bg-white p-4">
          <p className="text-sm text-secondary-500">Formulário não encontrado.</p>
        </section>
      )}

      <CollapsibleFormSection
        title="Perguntas do formulário"
        expanded={questionsExpanded}
        onToggle={() =>
          canEditContent
            ? toggleOrCancelQuestions(() => undefined)
            : questionsExpanded
              ? collapseQuestions(() => undefined)
              : expandQuestions()
        }
        isEditing={!!canEditContent}
        editingSummary={
          form
            ? `Filtradas pelos indicadores do ciclo e pelo nível ${audienceLabel(form.audience)}`
            : 'Selecione as perguntas do catálogo'
        }
        createSummary={
          form
            ? `Filtradas pelos indicadores do ciclo e pelo nível ${audienceLabel(form.audience)}`
            : 'Selecione as perguntas do catálogo'
        }
        className="mb-4"
      >
        <div className="px-4 pb-4 pt-3">
          {questionsLoading ? (
            <p className="text-sm text-secondary-500">Carregando perguntas...</p>
          ) : (
            <>
              <p className="mb-4 text-sm text-secondary-500">
                Selecione perguntas do catálogo, filtradas pelos indicadores do ciclo e pelo nível{' '}
                <strong>{audienceLabel(form?.audience)}</strong>.
              </p>

              {availableQuestions.length === 0 ? (
                <p className="text-sm text-secondary-500">
                  Nenhuma pergunta disponível. Defina indicadores do ciclo neste nível e cadastre perguntas nas competências.
                </p>
              ) : (
                <form onSubmit={(event) => void handleSaveQuestions(event)}>
                  <div className="space-y-4">
                    {questionsByIndicator.map((group) => (
                      <div key={group.indicatorName} className="rounded-lg border border-secondary-100 p-3">
                        <h3 className="mb-2 text-sm font-semibold text-secondary-900">{group.indicatorName}</h3>
                        <div className="space-y-2">
                          {group.questions.map((question) => (
                            <label key={question.competencyQuestionId} className="erp-label-inline items-start">
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={selectedQuestionIds.includes(question.competencyQuestionId)}
                                disabled={!canEditContent}
                                onChange={() => toggleQuestion(question.competencyQuestionId)}
                              />
                              <span>
                                <span className="block font-medium text-secondary-900">{question.competencyName}</span>
                                <span className="block text-sm text-secondary-600">{question.prompt}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {canEditContent ? (
                    <FormActionBar
                      onCancel={() => collapseQuestions(() => undefined)}
                      className="erp-btn-actions mt-4"
                    >
                      <button type="submit" className="erp-btn-primary" disabled={saving}>
                        {saving ? 'Salvando...' : 'Salvar perguntas'}
                      </button>
                    </FormActionBar>
                  ) : (
                    <p className="mt-4 text-sm text-secondary-500">
                      O ciclo não está em rascunho; as perguntas do formulário são somente leitura.
                    </p>
                  )}
                </form>
              )}
            </>
          )}
        </div>
      </CollapsibleFormSection>
    </PageShell>
  );
}
